(() => {
  'use strict';
  if (window.MBIJudgeMeCarouselDragLoaded) return;
  window.MBIJudgeMeCarouselDragLoaded = true;
  const selector = '.mbi-flexible-app-section--testimonial[data-mbi-testimonial-drag="true"] .jdgm-cards-carousel';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new WeakMap();

  function initialise(root) {
    const viewport = root.querySelector('.jdgm-cards-wrapper');
    const track = root.querySelector('.jdgm-videos-container');
    if (!viewport || !track) return;
    if (controllers.has(viewport)) {
      controllers.get(viewport)();
      return;
    }
    root.classList.add('mbi-judgeme-native-scroll');
    viewport.dataset.mbiDragReady = 'true';
    viewport.scrollLeft = 0;
    const label = root.closest('.mbi-flexible-app-section')?.querySelector('h1,h2,h3')?.textContent.trim();
    if (!viewport.hasAttribute('tabindex')) viewport.tabIndex = 0;
    if (label && !viewport.hasAttribute('aria-label')) {
      viewport.setAttribute('role', 'region');
      viewport.setAttribute('aria-label', label);
    }
    let pointer = null;
    let suppressClick = false;
    let clickTimer = 0;
    let updateFrame = 0;
    let animationFrame = 0;
    let animationTarget = null;
    let previousScrollBehavior = '';

    function update() {
      updateFrame = 0;
      const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const position = animationTarget ?? viewport.scrollLeft;
      for (const [name, disabled] of [
        ['Previous slide', position <= 2 || maximum <= 2],
        ['Next slide', position >= maximum - 2 || maximum <= 2],
      ]) {
        const button = root.querySelector('[aria-label="' + name + '"]');
        if (button) {
          button.disabled = disabled;
          button.setAttribute('aria-disabled', String(disabled));
        }
      }
    }

    function queueUpdate() {
      if (!updateFrame) updateFrame = requestAnimationFrame(update);
    }

    function stopAnimation() {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      if (animationTarget !== null) viewport.style.scrollBehavior = previousScrollBehavior;
      animationTarget = null;
    }

    function page(direction) {
      const card = track.querySelector('.jdgm-carousel-item:not(.jdgm-clone-before):not(.jdgm-clone-after)');
      if (!card) return;
      const width = card.getBoundingClientRect().width;
      if (width <= 0) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const target = Math.max(0, Math.min(maximum, (animationTarget ?? viewport.scrollLeft) + direction * (width + gap)));
      stopAnimation();
      const start = viewport.scrollLeft;
      previousScrollBehavior = viewport.style.scrollBehavior;
      viewport.style.scrollBehavior = 'auto';
      animationTarget = target;
      queueUpdate();
      if (motion.matches || Math.abs(target - start) < 1) {
        viewport.scrollLeft = target;
        stopAnimation();
        return;
      }
      let started = null;
      function animate(time) {
        if (!viewport.isConnected) { stopAnimation(); return; }
        if (started === null) started = time;
        const progress = motion.matches ? 1 : Math.min(1, (time - started) / 450);
        viewport.scrollLeft = start + (target - start) * (1 - Math.pow(1 - progress, 3));
        if (progress < 1) animationFrame = requestAnimationFrame(animate);
        else { stopAnimation(); queueUpdate(); }
      }
      animationFrame = requestAnimationFrame(animate);
    }

    root.addEventListener('pointerdown', event => {
      const name = event.target.closest('button')?.getAttribute('aria-label');
      if (event.pointerType === 'mouse' && event.button === 0 && (name === 'Previous slide' || name === 'Next slide')) event.preventDefault();
    }, true);

    root.addEventListener('click', event => {
      const button = event.target.closest('button');
      const name = button?.getAttribute('aria-label');
      if (name !== 'Previous slide' && name !== 'Next slide') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!button.disabled) page(name === 'Next slide' ? 1 : -1);
    }, true);

    function focusReview(event) {
      if (event.target.closest('a[href],button,input,select,textarea')) return false;
      const card = event.target.closest('.jdgm-carousel-item');
      if (!card) return false;
      if (!card.hasAttribute('tabindex')) card.tabIndex = -1;
      card.focus({ preventScroll: true });
      return true;
    }

    viewport.addEventListener('focusin', event => event.stopPropagation());

    viewport.addEventListener('pointerdown', event => {
      stopAnimation();
      queueUpdate();
      window.clearTimeout(clickTimer);
      suppressClick = false;
      if (pointer || event.button !== 0 || event.isPrimary === false) return;
      if (focusReview(event) && event.pointerType === 'mouse') event.preventDefault();
      if (event.pointerType !== 'mouse') return;
      if (event.target.closest('button,input,select,textarea')) return;
      if (viewport.scrollWidth <= viewport.clientWidth + 2) return;
      pointer = { id: event.pointerId, x: event.clientX, left: viewport.scrollLeft, moved: false };
    });

    viewport.addEventListener('pointermove', event => {
      if (!pointer || event.pointerId !== pointer.id) return;
      const distance = event.clientX - pointer.x;
      if (!pointer.moved && Math.abs(distance) < 7) return;
      if (!pointer.moved) {
        try { viewport.setPointerCapture(pointer.id); }
        catch (error) { pointer = null; return; }
        pointer.moved = true;
        viewport.classList.add('mbi-is-dragging');
      }
      viewport.scrollLeft = pointer.left - distance;
      event.preventDefault();
    });

    function finish(event) {
      if (!pointer || event.pointerId !== pointer.id) return;
      const finished = pointer;
      pointer = null;
      if (finished.moved) {
        suppressClick = true;
        clickTimer = window.setTimeout(() => { suppressClick = false; }, 400);
        if (viewport.hasPointerCapture(finished.id)) viewport.releasePointerCapture(finished.id);
      }
      viewport.classList.remove('mbi-is-dragging');
    }

    window.addEventListener('pointerup', finish, true);
    window.addEventListener('pointercancel', finish, true);
    viewport.addEventListener('lostpointercapture', event => {
      if (pointer?.id === event.pointerId) finish(event);
    });
    viewport.addEventListener('click', event => {
      if (suppressClick && event.detail !== 0) {
        suppressClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      focusReview(event);
    }, true);
    viewport.addEventListener('dragstart', event => event.preventDefault());
    for (const type of ['touchstart', 'touchmove', 'touchend']) {
      viewport.addEventListener(type, event => event.stopPropagation(), { capture: true, passive: true });
    }
    viewport.addEventListener('keydown', event => {
      if (event.target !== viewport || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
      event.preventDefault();
      page(event.key === 'ArrowRight' ? 1 : -1);
    });
    viewport.addEventListener('scroll', queueUpdate, { passive: true });
    viewport.addEventListener('wheel', () => { stopAnimation(); queueUpdate(); }, { passive: true });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => { stopAnimation(); queueUpdate(); });
      observer.observe(viewport);
      observer.observe(track);
    }
    controllers.set(viewport, queueUpdate);
    queueUpdate();
  }

  function scan(scope) {
    if (!scope?.querySelectorAll) return;
    scope.querySelectorAll(selector).forEach(initialise);
    if (scope.matches?.(selector)) initialise(scope);
    const owner = scope.closest?.(selector);
    if (owner) initialise(owner);
  }

  function start() {
    scan(document);
    document.addEventListener('pointerdown', () => {
      document.querySelectorAll(selector).forEach(root => { root.dataset.mbiPointerFocus = 'true'; });
    }, { capture: true, passive: true });
    document.addEventListener('keydown', () => {
      document.querySelectorAll(selector).forEach(root => { delete root.dataset.mbiPointerFocus; });
    }, true);
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) if (node.nodeType === 1) scan(node);
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', event => scan(event.target));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
